import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, Copy, Mail, LogOut, Trophy, Flame, Crown, ShieldPlus, Target, Footprints } from "lucide-react";
import { toPng } from "html-to-image";
import SquadShareCard from "../components/SquadShareCard";
import SharePopover from "../components/SharePopover";
import RankBadge from "../components/RankBadge";
import { SHARE_URL } from "../lib/share";

export default function Squad() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joinCode, setJoinCode] = useState(params.get("code")?.toUpperCase() || "");
    const [squadName, setSquadName] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [busy, setBusy] = useState(null);
    const cardRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/squad/me");
            setData(data);
        } catch (e) {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Auto-attempt join if ?code=XYZ123 is in URL on first load
    useEffect(() => {
        const code = params.get("code");
        if (code && data && !data.squad) {
            handleJoin(code.toUpperCase()).then(() => {
                params.delete("code");
                setParams(params, { replace: true });
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.squad]);

    const copyInvite = async () => {
        if (!data?.invite_code) return;
        try {
            await navigator.clipboard.writeText(data.invite_code);
            toast.success("Invite code copied");
        } catch (e) {
            toast.error("Could not copy");
        }
    };

    const shareInvite = async () => null; // legacy — SharePopover now handles squad invites

    // Returns the 1080×1080 PNG of the squad-invite card, ready to attach to
    // a native share sheet or trigger a download.
    const buildSquadFile = async () => {
        if (!cardRef.current) return null;
        await new Promise(r => requestAnimationFrame(r));
        const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 1, width: 1080, height: 1080, backgroundColor: "#0a0a0a" });
        const blob = await (await fetch(dataUrl)).blob();
        return new File([blob], `hypergains-squad-${(data?.invite_code || "invite").toLowerCase()}.png`, { type: "image/png" });
    };

    const downloadSquadCard = async () => {
        try {
            const file = await buildSquadFile();
            if (!file) throw new Error("no card");
            const a = document.createElement("a");
            const blobUrl = URL.createObjectURL(file);
            a.href = blobUrl;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
            toast.success("Squad invite card saved");
        } catch (e) {
            toast.error("Could not save the card");
        }
    };

    const handleCreate = async () => {
        setBusy("create");
        try {
            await api.post("/squad/create", { name: squadName });
            toast.success("Squad created");
            setSquadName("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create squad");
        } finally { setBusy(null); }
    };

    const handleJoin = async (code) => {
        const cc = (code || joinCode || "").trim().toUpperCase();
        if (!cc) { toast.error("Enter an invite code"); return; }
        setBusy("join");
        try {
            await api.post("/squad/join", { invite_code: cc });
            toast.success("Joined squad");
            setJoinCode("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not join squad");
        } finally { setBusy(null); }
    };

    const handleLeave = async () => {
        if (!window.confirm("Leave this squad? You can rejoin later with the invite code.")) return;
        setBusy("leave");
        try {
            await api.post("/squad/leave", {});
            toast.success("Left squad");
            await load();
        } catch (e) {
            toast.error("Could not leave");
        } finally { setBusy(null); }
    };

    const handleInviteEmail = async () => {
        const e = inviteEmail.trim();
        if (!e) { toast.error("Enter a friend's email"); return; }
        setBusy("email");
        try {
            await api.post("/squad/invite-email", { email: e });
            toast.success(`Invite sent to ${e}`);
            setInviteEmail("");
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Could not send invite");
        } finally { setBusy(null); }
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-5xl px-4 py-12">
                <div className="tactical-card p-6 text-sm text-muted-foreground" data-testid="squad-loading">Loading squad…</div>
            </div>
        );
    }

    const inviteCode = data?.invite_code || "";
    const squad = data?.squad;
    const leaderboard = data?.leaderboard || [];
    const ownerId = squad?.owner_user_id;
    const ownerName = leaderboard.find(m => m.user_id === ownerId)?.name;

    return (
        <div className="mx-auto max-w-5xl px-4 py-8 sm:py-12" data-testid="squad-page">
            {/* Off-screen 1080×1080 squad invite card */}
            <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", opacity: 0 }} aria-hidden="true">
                <SquadShareCard
                    ref={cardRef}
                    squadName={squad?.name || "Hypergains Squad"}
                    inviteCode={inviteCode}
                    ownerName={ownerName}
                />
            </div>
            {/* Hero */}
            <div className="relative overflow-hidden border border-border bg-card p-6 sm:p-10">
                <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-accent-red/10 blur-3xl" />
                <div className="relative">
                    <div className="label-eyebrow text-accent-red flex items-center gap-2">
                        <Users className="h-4 w-4" /> Your Squad
                    </div>
                    <h1 className="mt-3 font-display text-4xl font-black uppercase leading-[0.95] sm:text-5xl">
                        Train Together. <span className="text-accent-red">Suffer Together.</span>
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
                        5-person pods. Weekly EXP + tonnage leaderboard. No DMs, no fluff — just the receipts. Resets every Monday 00:00 UTC.
                    </p>
                </div>
            </div>

            {/* Invite code strip — always visible */}
            <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2 tactical-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <div className="label-eyebrow text-muted-foreground">Your invite code</div>
                        <div className="mt-1 font-display text-3xl font-black tracking-[0.4em] text-accent-red sm:text-4xl" data-testid="squad-my-code">{inviteCode || "————"}</div>
                        <div className="mt-1 text-xs text-muted-foreground">Share this. Up to 5 friends can join your pod.</div>
                    </div>
                    <div className="flex flex-none flex-col gap-2 sm:flex-row">
                        <SharePopover
                            text={`Join my Hypergains Squad — code: ${inviteCode}. 5 lifters, weekly leaderboard, no fluff.`}
                            url={inviteCode ? `${SHARE_URL}/?squad_code=${inviteCode}${ownerName ? `&from=${encodeURIComponent(ownerName)}` : ""}` : SHARE_URL}
                            getFile={buildSquadFile}
                            label="Share invite"
                            testIdPrefix="squad-share"
                        />
                        <button onClick={downloadSquadCard} data-testid="squad-save-card" className="flex items-center justify-center gap-2 rounded-sm border border-accent-red bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]">
                            <Trophy className="h-3.5 w-3.5" /> Save card
                        </button>
                        <button onClick={copyInvite} data-testid="squad-copy-code" className="flex items-center justify-center gap-2 rounded-sm border border-border bg-secondary px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-accent-red hover:text-accent-red">
                            <Copy className="h-3.5 w-3.5" /> Copy code
                        </button>
                    </div>
                </div>
                <div className="tactical-card p-5">
                    <div className="label-eyebrow text-muted-foreground flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Invite by email</div>
                    <input
                        type="email"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        placeholder="friend@gmail.com"
                        data-testid="squad-invite-email-input"
                        className="mt-2 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:border-accent-red focus:outline-none"
                    />
                    <button onClick={handleInviteEmail} disabled={busy === "email"} data-testid="squad-invite-email-send" className="mt-2 w-full rounded-sm border border-border bg-secondary px-3 py-2 text-xs font-bold uppercase tracking-wider hover:border-accent-red hover:text-accent-red disabled:opacity-60">
                        {busy === "email" ? "Sending…" : "Send invite"}
                    </button>
                    <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground/80">Email delivery requires a verified domain. Use Share invite for now.</p>
                </div>
            </div>

            {/* If user is in a squad, show leaderboard. Otherwise: create or join */}
            {squad ? (
                <section className="mt-8" data-testid="squad-leaderboard-section">
                    <div className="flex items-end justify-between gap-4 border-b border-border pb-3">
                        <div>
                            <div className="label-eyebrow text-accent-red">This Week's Leaderboard</div>
                            <h2 className="mt-1 font-display text-3xl font-black uppercase tracking-tight" data-testid="squad-name">{squad.name}</h2>
                            <div className="mt-1 text-xs text-muted-foreground">{leaderboard.length}/5 members</div>
                        </div>
                        <button onClick={handleLeave} data-testid="squad-leave" className="flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:border-accent-red hover:text-accent-red">
                            <LogOut className="h-3.5 w-3.5" /> Leave
                        </button>
                    </div>
                    <ol className="mt-5 space-y-3">
                        {leaderboard.map((m, i) => (
                            <li key={m.user_id} className={`flex flex-col gap-3 border bg-card p-4 ${i === 0 ? "border-accent-red shadow-[0_0_0_1px_var(--accent-red)]" : "border-border"}`} data-testid={`squad-row-${i}`}>
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                                    {/* Position + insignia */}
                                    <div className="flex flex-none items-center gap-3">
                                        <div className="flex h-10 w-10 flex-none items-center justify-center">
                                            {i === 0 ? <Crown className="h-5 w-5 text-accent-red" /> : <span className="font-display text-2xl font-black text-muted-foreground">{i + 1}</span>}
                                        </div>
                                        <div data-testid={`squad-row-badge-${i}`}>
                                            <RankBadge rankId={m.rank?.id} size="sm" />
                                        </div>
                                    </div>
                                    {/* Name + rank + tagline + mode/steps */}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                            <span className="truncate font-display text-lg font-black uppercase">{m.name}</span>
                                            {ownerId === m.user_id && <span className="label-eyebrow rounded-sm border border-accent-red px-1.5 py-0.5 text-accent-red">Owner</span>}
                                        </div>
                                        <div className="mt-0.5 truncate text-xs uppercase tracking-wider text-accent-red" data-testid={`squad-row-rank-${i}`}>
                                            {m.rank?.name} · {m.rank?.tier}
                                        </div>
                                        {m.rank?.tagline && (
                                            <p className="mt-1 line-clamp-2 text-xs italic text-foreground/75" data-testid={`squad-row-tagline-${i}`}>
                                                "{m.rank.tagline}"
                                            </p>
                                        )}
                                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                            <span className="inline-flex items-center gap-1" data-testid={`squad-row-mode-${i}`}>
                                                <Target className="h-2.5 w-2.5 text-accent-red" /> {(m.goal || "recomp").toUpperCase()}
                                            </span>
                                            {!!m.step_goal && (
                                                <span className="inline-flex items-center gap-1" data-testid={`squad-row-steps-${i}`}>
                                                    <Footprints className="h-2.5 w-2.5 text-accent-red" /> {m.step_goal.toLocaleString()}/day
                                                </span>
                                            )}
                                            <span className="inline-flex items-center gap-1">
                                                <Flame className="h-2.5 w-2.5 text-accent-red" /> {(m.total_exp || 0).toLocaleString()} EXP
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {/* Weekly stats */}
                                <div className="grid grid-cols-3 gap-3 border-t border-border/60 pt-3">
                                    <Cell icon={<Flame className="h-3 w-3" />} label="Week EXP" value={(m.week_exp || 0).toLocaleString()} accent />
                                    <Cell icon={<Trophy className="h-3 w-3" />} label="Sessions" value={m.week_workouts} />
                                    <Cell label="Tonnage" value={m.week_tonnage?.toLocaleString() || "0"} />
                                </div>
                            </li>
                        ))}
                    </ol>
                </section>
            ) : (
                <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="squad-empty-section">
                    <div className="tactical-card p-6">
                        <div className="flex items-center gap-2 label-eyebrow text-accent-red"><ShieldPlus className="h-4 w-4" /> Create your squad</div>
                        <h3 className="mt-2 font-display text-2xl font-black uppercase">Be the captain.</h3>
                        <p className="mt-1 text-sm text-muted-foreground">You'll own the squad. Invite up to 4 lifters with your code or by email.</p>
                        <input
                            type="text"
                            maxLength={40}
                            value={squadName}
                            onChange={e => setSquadName(e.target.value)}
                            placeholder="Squad name (e.g. 'Iron Anvils')"
                            data-testid="squad-create-name"
                            className="mt-4 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:border-accent-red focus:outline-none"
                        />
                        <button onClick={handleCreate} disabled={busy === "create"} data-testid="squad-create-btn" className="mt-2 w-full rounded-sm bg-accent-red px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)] disabled:opacity-60">
                            {busy === "create" ? "Creating…" : "Create Squad"}
                        </button>
                    </div>
                    <div className="tactical-card p-6">
                        <div className="flex items-center gap-2 label-eyebrow text-accent-red"><Users className="h-4 w-4" /> Join with a code</div>
                        <h3 className="mt-2 font-display text-2xl font-black uppercase">Got an invite?</h3>
                        <p className="mt-1 text-sm text-muted-foreground">Paste the 6-character code your friend shared.</p>
                        <input
                            type="text"
                            maxLength={8}
                            value={joinCode}
                            onChange={e => setJoinCode(e.target.value.toUpperCase())}
                            placeholder="ABC123"
                            data-testid="squad-join-code-input"
                            className="mt-4 w-full rounded-sm border border-border bg-background px-3 py-2 text-center font-display text-2xl font-black tracking-[0.4em] focus:border-accent-red focus:outline-none"
                        />
                        <button onClick={() => handleJoin()} disabled={busy === "join"} data-testid="squad-join-btn" className="mt-2 w-full rounded-sm border border-accent-red bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-accent-red hover:bg-accent-red hover:text-white disabled:opacity-60">
                            {busy === "join" ? "Joining…" : "Join Squad"}
                        </button>
                    </div>
                </section>
            )}

            <div className="mt-10 border border-dashed border-border bg-card/40 p-5 text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Leaderboard resets every Monday 00:00 UTC.</p>
                <p className="mt-1 text-sm italic text-foreground/70">No bots. No streaks. Just receipts.</p>
            </div>
        </div>
    );
}

function Cell({ icon, label, value, accent }) {
    return (
        <div className="min-w-0 sm:min-w-[110px]">
            <div className={`label-eyebrow flex items-center gap-1 truncate ${accent ? "text-accent-red" : "text-muted-foreground"}`}>{icon}{label}</div>
            <div className="mt-0.5 truncate font-display text-lg font-black tabular-nums">{value}</div>
        </div>
    );
}
